import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import moment from 'moment';
import { FormattedMessage } from 'react-intl';
import { getFirstMonthToCompare, getSecondMonthToCompare } from 'common/dashboards/_redux';
import MonthPicker from 'common/components/MonthPicker';
import ColumnChart from 'common/dashboards/_components/ColumnChart';

import { withTheme } from 'common/styling/theme';
import { staticStyles, getDynamicStyles } from './style';

const getMonthRange = ({ year, month }) => {
  const start = moment({ year, month: month - 1, day: 1 }).format('YYYY-MM-DD');

  const end = moment({ year, month: month - 1 })
    .endOf('month')
    .format('YYYY-MM-DD');

  return { start, end };
};

const months = moment.monthsShort();
const beautifyFormat = value => `${months[value.month - 1]}. ${value.year}`;

const MonthColumnChart = ({
  theme,
  partnerId,
  firstMonthToCompare,
  secondMonthToCompare,
  getFirstMonthToCompare,
  getSecondMonthToCompare,
  sampleType,
  date,
  brand,
  currentMonthFunnel,
  lastMonthFunnel,
  isClientInterface,
}) => {
  const dynamicStyles = Object.keys(theme).length ? getDynamicStyles(theme) : ` `;

  const currentYear = Number(moment(date).format('YYYY'));
  const currentMonth = Number(moment(date).format('M'));
  const lastMonthYear = Number(moment(date).subtract(1, 'months').format('YYYY'));
  const lastMonth = Number(moment(date).subtract(1, 'months').format('M'));

  const [firstMonthValue, setFirstMonthValue] = useState({
    year: currentYear,
    month: currentMonth,
  });

  const [secondMonthValue, setSecondMonthValue] = useState({
    year: lastMonthYear,
    month: lastMonth,
  });

  let firstMonthData = [];
  let secondMonthData = [];

  const fieldsForCompare = [
    'joined_users',
    'users_created_real_account',
    'users_created_demo_account',
    'deposited_users',
  ];

  if (firstMonthToCompare?.aggregated_data) {
    firstMonthData = fieldsForCompare.map(field => firstMonthToCompare.aggregated_data[field]);
  } else if (
    date.clone().isSame(moment({ year: firstMonthValue.year, month: firstMonthValue.month - 1 }), 'month') &&
    currentMonthFunnel?.aggregated_data
  ) {
    firstMonthData = fieldsForCompare.map(field => currentMonthFunnel.aggregated_data[field]);
  }

  if (secondMonthToCompare?.aggregated_data) {
    secondMonthData = fieldsForCompare.map(field => secondMonthToCompare.aggregated_data[field]);
  } else if (
    date
      .clone()
      .subtract(1, 'month')
      .isSame(moment({ year: secondMonthValue.year, month: secondMonthValue.month - 1 }), 'month') &&
    lastMonthFunnel?.aggregated_data
  ) {
    secondMonthData = fieldsForCompare.map(field => lastMonthFunnel.aggregated_data[field]);
  }

  const handleFirstMonthChange = monthValue => {
    setFirstMonthValue(monthValue);
    getFirstMonthToCompare({
      startDate: getMonthRange(monthValue).start,
      endDate: getMonthRange(monthValue).end,
      accountableUsers: partnerId,
      sampleType,
      brand,
      isClientInterface,
      ...(isClientInterface && { referType: 'refer_partner' }),
    });
  };

  const handleSecondMonthChange = monthValue => {
    setSecondMonthValue(monthValue);
    getSecondMonthToCompare({
      startDate: getMonthRange(monthValue).start,
      endDate: getMonthRange(monthValue).end,
      accountableUsers: partnerId,
      sampleType,
      brand,
      isClientInterface,
      ...(isClientInterface && { referType: 'refer_partner' }),
    });
  };

  return (
    <div className="MonthColumnChart__wrapper">
      <div className="MonthColumnChart__top-line">
        <div className="MonthColumnChart__compare-block">
          <MonthPicker
            monthValue={firstMonthValue}
            years={{ min: { year: 2010, month: 1 }, max: { year: currentYear, month: currentMonth } }}
            handleChange={handleFirstMonthChange}
          />
          <FormattedMessage id="justCompareWith">
            {txt => <span className="MonthColumnChart__compare-block-text">{txt}</span>}
          </FormattedMessage>
          <MonthPicker
            monthValue={secondMonthValue}
            years={{ min: { year: 2010, month: 1 }, max: { year: currentYear, month: currentMonth } }}
            handleChange={handleSecondMonthChange}
          />
        </div>
      </div>
      <div className="MonthColumnChart__inner">
        <ColumnChart
          firstMonthTitle={beautifyFormat(firstMonthValue)}
          firstMonthData={firstMonthData}
          secondMonthTitle={beautifyFormat(secondMonthValue)}
          secondMonthData={secondMonthData}
        />
      </div>

      <style jsx>{staticStyles}</style>
      <style jsx>{dynamicStyles}</style>
    </div>
  );
};

MonthColumnChart.propTypes = {
  theme: PropTypes.object,
  firstMonthToCompare: PropTypes.object,
  secondMonthToCompare: PropTypes.object,
  getFirstMonthToCompare: PropTypes.func.isRequired,
  getSecondMonthToCompare: PropTypes.func.isRequired,
  partnerId: PropTypes.number,
  sampleType: PropTypes.string.isRequired,
  date: PropTypes.object.isRequired,
  brand: PropTypes.string,
  currentMonthFunnel: PropTypes.object,
  lastMonthFunnel: PropTypes.object,
  isClientInterface: PropTypes.bool,
};

MonthColumnChart.defaultProps = {
  theme: {},
  firstMonthToCompare: {},
  secondMonthToCompare: {},
  currentMonthFunnel: {},
  lastMonthFunnel: {},
  partnerId: 0,
  brand: undefined,
  isClientInterface: false,
};

export default compose(
  withTheme(),
  connect(
    state => ({
      firstMonthToCompare: state.dashboards.firstMonthToCompare,
      secondMonthToCompare: state.dashboards.secondMonthToCompare,
      currentMonthFunnel: state.dashboards.currentMonthFunnel,
      lastMonthFunnel: state.dashboards.lastMonthFunnel,
    }),
    {
      getFirstMonthToCompare: data => getFirstMonthToCompare.request(data),
      getSecondMonthToCompare: data => getSecondMonthToCompare.request(data),
    }
  )
)(MonthColumnChart);
export { MonthColumnChart };
